/*
  formEngine — v2.0 runtime rules for onboarding + assessment forms.

  Rules implemented here (spec §4.3):

  showIf
    Conditional field visibility. Shape (as shipped in the seven
    v2.0 forms):
        { fieldId: "onero_dexa_ever", operator: "equals", value: "Yes" }
    We also support compound conditions via `allOf` / `anyOf`, and
    a small operator set so future spec bumps don't break us:
        equals, not_equals, gt, gte, lt, lte, in, not_in,
        is_truthy, is_falsy.

  exclusiveGroup
    A string name shared by two or more fields. Selecting one
    clears every other member of the group (radio-like behaviour
    across a set of otherwise-independent inputs).

  exclusiveWith
    An array of field ids on THIS field. Setting this field to a
    truthy value clears every field in that array. Bidirectional
    behaviour is achieved simply by both fields listing each other
    (which is what the seed forms do).

  safetyCritical + alertRule
    Boolean flag + rule name. When the field is answered "truthy"
    (see `isSafetyTruthy`), the engine reports a runtime alert so
    the filler UI can surface a red banner and the assessment /
    clinician review can highlight the row.

  All functions are pure. `applyAnswerPatch` returns a new
  `answers` object; nothing is mutated in place.
*/

// ---------------------------------------------------------------------------
// showIf
// ---------------------------------------------------------------------------

function _cmp(op, actual, expected) {
  switch (op) {
    case "equals":
      // Loose equality so numbers-as-strings still match ("2" == 2).
      // eslint-disable-next-line eqeqeq
      return actual == expected;
    case "not_equals":
      // eslint-disable-next-line eqeqeq
      return actual != expected;
    case "gt":  return Number(actual) >   Number(expected);
    case "gte": return Number(actual) >=  Number(expected);
    case "lt":  return Number(actual) <   Number(expected);
    case "lte": return Number(actual) <=  Number(expected);
    case "in":
      return Array.isArray(expected) && expected.some((v) => String(v) === String(actual));
    case "not_in":
      return Array.isArray(expected) && !expected.some((v) => String(v) === String(actual));
    case "is_truthy":
      return Boolean(actual) && actual !== "" && actual !== "No" && actual !== "no";
    case "is_falsy":
      return !actual || actual === "" || actual === "No" || actual === "no";
    default:
      // Unknown operator → fail-open (show the field) so an
      // author-side typo doesn't lock a client out of a section.
      // eslint-disable-next-line no-console
      console.warn(`[formEngine] unknown showIf operator: ${op}`);
      return true;
  }
}

/** Pass `fields` so rules may name an option label ("Yes") while the
 *  answer stores the option value (1). */
export function evaluateShowIf(field, answers, fields) {
  const cond = field?.showIf;
  if (!cond) return true;
  return _evalCond(cond, answers, fields);
}

const LABEL_AWARE_OPS = new Set(["equals", "not_equals", "in", "not_in"]);

function _withLabel(fieldId, actual, fields) {
  if (actual === undefined || actual === null || actual === "") return [actual];
  const field = (fields || []).find((f) => f?.id === fieldId);
  const opt = (field?.options || []).find((o) => String(o?.value) === String(actual));
  return opt ? [actual, opt.label] : [actual];
}

function _evalCond(cond, answers, fields) {
  if (!cond || typeof cond !== "object") return true;
  if (Array.isArray(cond.allOf)) {
    return cond.allOf.every((c) => _evalCond(c, answers, fields));
  }
  if (Array.isArray(cond.anyOf)) {
    return cond.anyOf.some((c) => _evalCond(c, answers, fields));
  }
  const { fieldId, operator, value } = cond;
  if (!fieldId) return true;
  const op = operator || "equals";
  const actual = answers?.[fieldId];
  if (!LABEL_AWARE_OPS.has(op)) return _cmp(op, actual, value);
  const base = op === "equals" || op === "not_equals" ? "equals" : "in";
  const hit = _withLabel(fieldId, actual, fields).some((c) => _cmp(base, c, value));
  return op === "equals" || op === "in" ? hit : !hit;
}

/** Filter a `fields` list down to just the ones that should render
 *  under the current `answers`. Reactive — the caller re-invokes
 *  after every answer change.
 */
export function visibleFields(fields, answers) {
  return (fields || []).filter((f) => evaluateShowIf(f, answers, fields));
}

// ---------------------------------------------------------------------------
// exclusiveGroup / exclusiveWith
// ---------------------------------------------------------------------------

function _isBlank(v) {
  return v === null || v === undefined || v === "" || v === false;
}

/** Return a new answers object where setting `fieldId = value`
 *  also clears any other field that is mutually exclusive with
 *  it (via shared exclusiveGroup OR listed in exclusiveWith on
 *  either side).
 *
 *  Only fires when `value` is truthy — clearing a field never
 *  triggers a mutex sweep.
 */
export function applyAnswerPatch(fields, answers, fieldId, value) {
  const patched = { ...(answers || {}), [fieldId]: value };
  if (_isBlank(value)) return patched;

  const byId = new Map((fields || []).map((f) => [f.id, f]));
  const src = byId.get(fieldId);
  if (!src) return patched;

  // exclusiveGroup — clear peers.
  const group = src.exclusiveGroup;
  if (group) {
    for (const f of fields) {
      if (f.id !== fieldId && f.exclusiveGroup === group) {
        patched[f.id] = _clearValueFor(f);
      }
    }
  }

  // exclusiveWith on src — clear each listed field.
  if (Array.isArray(src.exclusiveWith)) {
    for (const otherId of src.exclusiveWith) {
      if (byId.has(otherId)) {
        patched[otherId] = _clearValueFor(byId.get(otherId));
      }
    }
  }

  // Reverse-direction exclusiveWith — someone else may list us.
  for (const f of fields) {
    if (f.id === fieldId) continue;
    if (Array.isArray(f.exclusiveWith) && f.exclusiveWith.includes(fieldId)) {
      patched[f.id] = _clearValueFor(f);
    }
  }
  return patched;
}

function _clearValueFor(field) {
  // Preserve the shape the collector expects: checkboxes clear to
  // false, multi-selects to [], everything else to "".
  if (field.type === "checkbox") return false;
  if (field.type === "multi" || field.type === "multi-select") return [];
  return "";
}

// ---------------------------------------------------------------------------
// safetyCritical + alertRule
// ---------------------------------------------------------------------------

/** A safety-critical field is considered "triggered" when the
 *  answer is truthy in the human sense — checkboxes ticked, radios
 *  answered "Yes", ratings >0, text non-empty. Empty numbers / "No"
 *  / blank strings do not trigger.
 */
export function isSafetyTruthy(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "boolean") return value === true;
  if (typeof value === "number") return value > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (!s) return false;
    if (["no", "none", "n/a", "na", "false", "0"].includes(s)) return false;
    return true;
  }
  return Boolean(value);
}

/** Return the list of triggered safety-critical fields. Only
 *  considers fields that are currently visible.
 */
export function getSafetyAlerts(fields, answers) {
  const out = [];
  for (const f of fields || []) {
    if (!f.safetyCritical) continue;
    if (!evaluateShowIf(f, answers, fields)) continue;
    if (isSafetyTruthy(answers?.[f.id])) {
      out.push({
        fieldId: f.id,
        label: f.label || f.id,
        rule: f.alertRule || "review_before_appointment",
        value: answers[f.id],
      });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Small human-readable renderer for the safety alert card.
// ---------------------------------------------------------------------------

export const ALERT_RULE_MESSAGES = {
  review_before_appointment:
    "A clinician will review this before your appointment.",
  require_gp_review:
    "Please have your GP review this ahead of your visit.",
  require_immediate_call:
    "Please call the clinic to discuss before your appointment.",
};
